<script setup lang="ts">
import { computed } from 'vue'
import {
  itemScopeAttributes,
  useModelStrings,
  type DetailTemplateProps,
} from '@jantm/model-core'
import type {Invoice} from "@/types/Wiser/invoice.ts";
import DocumentsComponent from "@/components/DocumentsComponent.vue";
import InvoiceLinesComponent from "@/components/InvoiceLinesComponent.vue";

const props = withDefaults(defineProps<DetailTemplateProps<Invoice>>(), { headingLevel: 2 })

const { t, core, locale, detailLabel } = useModelStrings(() => props.definition)

const scope = computed(() => itemScopeAttributes(props.definition))
const heading = computed(() => `h${props.headingLevel}` as const)
const regionLabel = computed(() => props.ariaLabel ?? detailLabel.value)
</script>

<template>
  <section
    class="wiser-detail invoice-detail"
    role="region"
    :aria-label="regionLabel"
    :aria-busy="loading && Boolean(entity)"
    v-bind="scope"
  >
    <p
      v-if="error"
      class="wiser-detail__error invoice-detail__error"
      role="alert"
    >
      {{ core('state.error', { fallback: 'Something went wrong.' }) }}
    </p>
    <p
      v-else-if="loading && !entity"
      role="status"
    >
      {{ core('state.loading', { fallback: 'Loading…' }) }}
    </p>
    <p
      v-else-if="!entity"
      role="status"
    >
      {{ core('state.notFound', { fallback: 'Not found.' }) }}
    </p>

    <template v-else>
      <!--
        The brand and model together are the accessible name of this record, so
        they are the heading — not decoration above it. The heading level comes
        from the caller so the page outline stays intact wherever it is used.
      -->
      <component
        :is="heading"
        class="wiser-detail__title invoice-detail__title"
      >
        <span>{{ entity.omschrijving }}</span>
        <span class="wiser-item__badge invoice-item__badge">{{ entity.factuurnummer }}</span>
        <a class="btn btn-primary float-right" :href="`/api/wiser/generateInvoice/${entity.id}?print=true`" onclick="this.target='_blank'">Print</a>
      </component>

      <div class="wiser-detail__description invoice-detail__description">
        <h3>Gegevens</h3>
        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.naam">
          <dt>{{ t('model.invoice.field.naam', { fallback: 'Naam' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field invoice-detail__field"
              itemprop="identifier"
            >{{ entity.naam }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.contactpersoon">
          <dt>{{ t('model.invoice.field.contactpersoon', { fallback: 'Contactpersoon' }) }}</dt>
          <dd>
            <span>{{ entity.contactpersoon }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.adres || entity.huisnummer || entity.toevoeging || entity.postcode || entity.woonplaats">
          <dt>{{ t('model.invoice.field.adres', { fallback: 'Adres' }) }}</dt>
          <dd>
            <span>{{ entity.adres }} {{ entity.huisnummer }} {{ entity.toevoeging }}, {{ entity.postcode }} {{ entity.woonplaats }}</span>
            <span v-if="entity.land && 'NL' !== entity.land">[{{ entity.land }}]</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.btwnummer">
          <dt>{{ t('model.invoice.field.btwnummer', { fallback: 'btwnummer' }) }}</dt>
          <dd>
            <span>{{ entity.btwnummer }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.referentie">
          <dt>{{ t('model.invoice.field.referentie', { fallback: 'Referentie' }) }}</dt>
          <dd>
            <span>{{ entity.referentie }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.vestiging">
          <dt>{{ t('model.invoice.field.vestiging', { fallback: 'Vestiging' }) }}</dt>
          <dd>
            <span>{{ entity.vestiging }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.factuurdatum">
          <dt>{{ t('model.invoice.field.factuurdatum', { fallback: 'Factuurdatum' }) }}</dt>
          <dd>
            <span>{{ entity.factuurdatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.vervaldatum">
          <dt>{{ t('model.invoice.field.vervaldatum', { fallback: 'Vervaldatum' }) }}</dt>
          <dd>
            <span>{{ entity.vervaldatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.added_on">
          <dt>{{ t('model.invoice.field.added_on', { fallback: 'Toevoegingsdatum' }) }}</dt>
          <dd>
            <span v-html="entity.added_on" />
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity?.changed_on">
          <dt>{{ t('model.invoice.field.changed_on', { fallback: 'Wijzigingsdatum' }) }}</dt>
          <dd>
            <span v-html="entity.changed_on" />
          </dd>
        </dl>
      </div>

      <InvoiceLinesComponent class="wiser-detail__description relation-detail__description" v-if="entity.factuurregelsonderfactuur?.length ?? 0 > 0" :entityId="entity.id" />

      <div class="wiser-detail__description invoice-detail__description">
        <h3>Financieel</h3>
        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.is_gecrediteerd">
          <dt>{{ t('model.invoice.field.is_gecrediteerd', { fallback: 'Is gecrediteerd' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field invoice-detail__field"
              itemprop="identifier"
            >{{ entity.is_gecrediteerd }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.is_credit">
          <dt>{{ t('model.invoice.field.is_credit', { fallback: 'Credit' }) }}</dt>
          <dd>
            <span>{{ entity.is_credit }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field" v-if="entity.definitief">
          <dt>{{ t('model.invoice.field.definitief', { fallback: 'Definitief' }) }}</dt>
          <dd>
            <span>{{ entity.definitief }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field">
          <dt>{{ t('model.invoice.field.subtotaal', { fallback: 'Subtotaal' }) }}</dt>
          <dd>
            <span>{{ Intl.NumberFormat('nl-NL', { style: "currency", currency: "EUR" }).format(entity.subtotaal ?? 0) }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field">
          <dt>{{ t('model.invoice.field.btw', { fallback: 'BTW' }) }}</dt>
          <dd>
            <span>{{ Intl.NumberFormat('nl-NL', { style: "currency", currency: "EUR" }).format(entity.btw ?? 0) }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field">
          <dt>{{ t('model.invoice.field.totaal', { fallback: 'Totaal' }) }}</dt>
          <dd>
            <span>{{ Intl.NumberFormat('nl-NL', { style: "currency", currency: "EUR" }).format(entity.totaal ?? 0) }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field invoice-detail__field">
          <dt>{{ t('model.invoice.field.openstaand', { fallback: 'Openstaand' }) }}</dt>
          <dd>
            <span>{{ Intl.NumberFormat('nl-NL', { style: "currency", currency: "EUR" }).format(entity.openstaand ?? 0) }}</span>
          </dd>
        </dl>
      </div>

      <DocumentsComponent :entityId="entity.id" />
    </template>
  </section>
</template>
